export interface BadgeRingSegment {
    id: string
    name?: string
    value: number
    color: string
    enabled: boolean
}

export interface BadgeRingArc extends BadgeRingSegment {
    startAngle: number
    endAngle: number
    angle: number
}

function finiteNumber(value: unknown, fallback = 0): number {
    const number = Number(value)
    return Number.isFinite(number) ? number : fallback
}

export function normalizeRingSegment(segment: Partial<BadgeRingSegment>, index = 0): BadgeRingSegment {
    return {
        id: String(segment.id || `segment-${index + 1}`),
        name: String(segment.name || `Segment ${index + 1}`),
        value: Math.max(0, finiteNumber(segment.value)),
        color: String(segment.color || '#ffffffff'),
        enabled: segment.enabled !== false,
    }
}

export function calculateRingArcs(segments: Partial<BadgeRingSegment>[], startAngle = 0): BadgeRingArc[] {
    const active = (Array.isArray(segments) ? segments : [])
        .map(normalizeRingSegment)
        .filter(segment => segment.enabled && segment.value > 0)
    const total = active.reduce((sum, segment) => sum + segment.value, 0)
    if (total <= 0) return []

    const start = finiteNumber(startAngle)
    let cursor = start
    return active.map((segment, index) => {
        const endAngle = index === active.length - 1
            ? start + 360
            : cursor + (segment.value / total) * 360
        const arc = { ...segment, startAngle: cursor, endAngle, angle: endAngle - cursor }
        cursor = endAngle
        return arc
    })
}

export function updateRingSegment(
    segments: Partial<BadgeRingSegment>[],
    id: string,
    patch: Partial<Omit<BadgeRingSegment, 'id'>>,
): BadgeRingSegment[] {
    return (Array.isArray(segments) ? segments : []).map((segment, index) => {
        const normalized = normalizeRingSegment(segment, index)
        return normalized.id === id ? normalizeRingSegment({ ...normalized, ...patch, id }, index) : normalized
    })
}
